import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import FeedbackModal from "../components/ui/FeedbackModal";

const getPasswordStrength = (password) => {
  const issues = [];

  if (password.length < 8) {
    issues.push("At least 8 characters");
  }

  if (!/[a-z]/.test(password)) {
    issues.push("One lowercase letter");
  }

  if (!/[A-Z]/.test(password)) {
    issues.push("One uppercase letter");
  }

  if (!/[0-9]/.test(password)) {
    issues.push("One number");
  }

  if (!/[^A-Za-z0-9]/.test(password)) {
    issues.push("One special character");
  }

  let label = "Weak";

  if (issues.length === 0) {
    label = "Strong";
  } else if (issues.length <= 2) {
    label = "Medium";
  }

  return {
    label,
    issues,
    score: Math.max(0, 5 - issues.length),
  };
};

const getInitials = (name = "User") => {
  return String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

function EyeIcon({ open }) {
  return open ? (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <circle
        cx="12"
        cy="12"
        r="3"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path
        d="M3 3l18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-4.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M6.2 6.2C4.2 7.7 2.8 10 2 12c1.8 4.5 6.2 7 10 7 1.6 0 3.2-.4 4.6-1.1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M9.9 4.3C10.6 4.1 11.3 4 12 4c4 0 8.2 2.6 10 8-.5 1.2-1.1 2.4-1.9 3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LoadingProfile() {
  return (
    <div className="p-6">
      <div className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex animate-pulse items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-slate-200 dark:bg-slate-800" />

          <div className="space-y-2">
            <div className="h-5 w-32 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="h-3 w-52 rounded bg-slate-100 dark:bg-slate-800" />
          </div>
        </div>

        <div className="mt-8 space-y-5">
          <div className="h-12 rounded-xl bg-slate-100 dark:bg-slate-800" />
          <div className="h-12 rounded-xl bg-slate-100 dark:bg-slate-800" />
          <div className="h-64 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);

  const [name, setName] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  useEffect(() => {
    const loadUser = async () => {
      try {
        const savedUser = localStorage.getItem("user");

        if (savedUser) {
          const parsedUser = JSON.parse(savedUser);

          setUser(parsedUser);
          setName(parsedUser.name || "");
        }

        const response = await api.get("/auth/me");
        const freshUser = response.data.user;

        setUser(freshUser);
        setName(freshUser.name || "");

        localStorage.setItem("user", JSON.stringify(freshUser));
      } catch {
        const savedUser = localStorage.getItem("user");

        if (savedUser) {
          const parsedUser = JSON.parse(savedUser);

          setUser(parsedUser);
          setName(parsedUser.name || "");
        }
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const passwordStrength = useMemo(
    () => getPasswordStrength(newPassword),
    [newPassword]
  );

  const passwordTouched = Boolean(
    oldPassword || newPassword || confirmPassword
  );

  const passwordsMatch =
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword;

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-600";

  const passwordInputClass = `${inputClass} pr-12`;

  const closeFeedback = () => {
    setFeedback((previous) => ({
      ...previous,
      open: false,
    }));
  };

  const clearPasswordFields = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");

    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    const trimmedName = name.trim();

    if (!trimmedName) {
      setFeedback({
        open: true,
        type: "error",
        title: "Invalid Name",
        message: "Full name is required.",
      });

      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: trimmedName,
      };

      if (passwordTouched) {
        payload.oldPassword = oldPassword;
        payload.newPassword = newPassword;
        payload.confirmPassword = confirmPassword;
      }

      const response = await api.put(
        "/auth/update-profile",
        payload
      );

      const updatedUser = response.data.user;

      localStorage.setItem(
        "user",
        JSON.stringify(updatedUser)
      );

      setUser(updatedUser);
      setName(updatedUser?.name || trimmedName);

      window.dispatchEvent(
        new Event("user-updated")
      );

      clearPasswordFields();

      setFeedback({
        open: true,
        type: "success",
        title: "Profile Updated",
        message:
          response.data.message ||
          "Profile updated successfully.",
      });
    } catch (error) {
      setFeedback({
        open: true,
        type: "error",
        title: "Update Failed",
        message:
          error.response?.data?.message ||
          "Unable to update your profile.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingProfile />;
  }

  return (
    <div className="min-h-full p-6 text-slate-900 transition-colors duration-300 dark:text-slate-100">
      <FeedbackModal
        open={feedback.open}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={closeFeedback}
      />

      <div className="mx-auto max-w-3xl">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/20">
          <div className="border-b border-slate-100 p-6 dark:border-slate-800 sm:p-8">
            <div className="flex items-center gap-4">
              <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 text-lg font-bold text-white shadow-lg shadow-blue-600/20">
                {getInitials(user?.name || "User")}
              </div>

              <div className="min-w-0">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  My Profile
                </h2>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Manage your account information and password.
                </p>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleUpdate}
            className="space-y-7 p-6 sm:p-8"
          >
            <section>
              <div className="mb-5">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  Personal Information
                </h3>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Update the name associated with your account.
                </p>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="profile-name"
                    className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    Full Name
                  </label>

                  <input
                    id="profile-name"
                    type="text"
                    value={name}
                    onChange={(event) =>
                      setName(event.target.value)
                    }
                    placeholder="Enter full name"
                    autoComplete="name"
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="profile-email"
                    className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    Email Address
                  </label>

                  <input
                    id="profile-email"
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-500 outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                  />

                  <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500">
                    Email address cannot be changed here.
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-950/50">
              <div className="mb-5">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  Change Password
                </h3>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Leave these fields empty to keep your current password.
                </p>
              </div>

              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="old-password"
                    className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    Current Password
                  </label>

                  <div className="relative">
                    <input
                      id="old-password"
                      type={
                        showOldPassword
                          ? "text"
                          : "password"
                      }
                      value={oldPassword}
                      onChange={(event) =>
                        setOldPassword(
                          event.target.value
                        )
                      }
                      placeholder="Enter current password"
                      autoComplete="current-password"
                      className={passwordInputClass}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowOldPassword(
                          (previous) => !previous
                        )
                      }
                      aria-label={
                        showOldPassword
                          ? "Hide current password"
                          : "Show current password"
                      }
                      className="absolute inset-y-0 right-3 flex items-center px-1 text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200"
                    >
                      <EyeIcon open={showOldPassword} />
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="new-password"
                    className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    New Password
                  </label>

                  <div className="relative">
                    <input
                      id="new-password"
                      type={
                        showNewPassword
                          ? "text"
                          : "password"
                      }
                      value={newPassword}
                      onChange={(event) =>
                        setNewPassword(
                          event.target.value
                        )
                      }
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      className={passwordInputClass}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowNewPassword(
                          (previous) => !previous
                        )
                      }
                      aria-label={
                        showNewPassword
                          ? "Hide new password"
                          : "Show new password"
                      }
                      className="absolute inset-y-0 right-3 flex items-center px-1 text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200"
                    >
                      <EyeIcon open={showNewPassword} />
                    </button>
                  </div>

                  {newPassword ? (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                      <div className="flex items-center justify-between gap-3 text-xs">
                        <span className="font-medium text-slate-600 dark:text-slate-400">
                          Password strength
                        </span>

                        <span
                          className={`font-semibold ${
                            passwordStrength.label ===
                            "Strong"
                              ? "text-emerald-600 dark:text-emerald-400"
                              : passwordStrength.label ===
                                  "Medium"
                                ? "text-amber-600 dark:text-amber-400"
                                : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {passwordStrength.label}
                        </span>
                      </div>

                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            passwordStrength.label ===
                            "Strong"
                              ? "bg-emerald-500"
                              : passwordStrength.label ===
                                  "Medium"
                                ? "bg-amber-500"
                                : "bg-red-500"
                          }`}
                          style={{
                            width: `${
                              (passwordStrength.score /
                                5) *
                              100
                            }%`,
                          }}
                        />
                      </div>

                      {passwordStrength.issues.length >
                      0 ? (
                        <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                          Required:{" "}
                          {passwordStrength.issues.join(
                            ", "
                          )}
                          .
                        </p>
                      ) : (
                        <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                          Password meets all requirements.
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>

                <div>
                  <label
                    htmlFor="confirm-password"
                    className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    Confirm New Password
                  </label>

                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={
                        showConfirmPassword
                          ? "text"
                          : "password"
                      }
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(
                          event.target.value
                        )
                      }
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                      className={`${passwordInputClass} ${
                        confirmPassword &&
                        passwordsMatch
                          ? "border-emerald-400 dark:border-emerald-500"
                          : confirmPassword &&
                              !passwordsMatch
                            ? "border-red-400 dark:border-red-500"
                            : ""
                      }`}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(
                          (previous) => !previous
                        )
                      }
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirmed password"
                          : "Show confirmed password"
                      }
                      className="absolute inset-y-0 right-3 flex items-center px-1 text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-200"
                    >
                      <EyeIcon
                        open={showConfirmPassword}
                      />
                    </button>
                  </div>

                  {confirmPassword ? (
                    <p
                      className={`mt-1.5 text-xs font-medium ${
                        passwordsMatch
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {passwordsMatch
                        ? "Passwords match"
                        : "Passwords do not match"}
                    </p>
                  ) : null}
                </div>
              </div>
            </section>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 dark:border-slate-800 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={clearPasswordFields}
                disabled={!passwordTouched}
                className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Clear Password
              </button>

              <button
                type="submit"
                disabled={saving || !name.trim()}
                className="inline-flex min-w-[150px] items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Saving...
                  </>
                ) : (
                  "Update Profile"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}